import { useEffect, useState, type ComponentProps } from "react";
import useSearch from "src/utils/hooks/useSearch";
import { useFeature } from "src/feature/useFeature";
import { DisplayAd } from "../DisplayAd/DisplayAd";
import { TieredDisplayAd } from "../DisplayAd/TieredDisplayAd";
import { isSizeDimension } from "../DisplayAd/utils";
import { useRefreshOnPaneChange } from "./useRefreshOnPaneChange";

const REFRESH_TIMER = 5 * 1000; // 5 seconds

const AdVariant = {
  DisplayAd,
  TieredDisplayAd,
} as const;

type AdType = keyof typeof AdVariant;
type RefreshingAdProps<T extends AdType> = {
  variant: T;
  refreshThreshold?: number;
  adHeight?: number;
} & ComponentProps<(typeof AdVariant)[T]>;

export function RefreshingAd({
  variant,
  refreshThreshold = REFRESH_TIMER,
  adHeight,
  ...props
}: RefreshingAdProps<AdType>) {
  const { slotConfigId, onAdViewed, onNotFilled, onFilled } = props;
  const { origin, destination } = useSearch();
  const [minHeight, setMinHeight] = useState(0);
  const [prevOrigin, setPrevOrigin] = useState<string | undefined>();
  const [prevDestination, setPrevDestination] = useState<string | undefined>();
  const refreshAdUnits = useFeature("RefreshAdUnits");

  const AdComponent = AdVariant[variant];

  const { handleViewChange, refreshCount, forceRefresh } =
    useRefreshOnPaneChange({
      refreshThreshold,
      slotConfigId,
    });

  useEffect(() => {
    if (!prevOrigin && !prevDestination) {
      setPrevOrigin(origin?.canonicalName);
      setPrevDestination(destination?.canonicalName);
    }

    // Refresh ad if the origin or destination changes and the RefreshAdUnits feature is enabled.
    if (
      (origin?.canonicalName !== prevOrigin ||
        destination?.canonicalName !== prevDestination) &&
      refreshAdUnits
    ) {
      forceRefresh();
      setPrevOrigin(origin?.canonicalName);
      setPrevDestination(destination?.canonicalName);
    }
  }, [
    origin,
    destination,
    prevOrigin,
    prevDestination,
    refreshAdUnits,
    forceRefresh,
  ]);

  return (
    <div
      className="transition-[min-height] duration-500 ease-in-out"
      style={{ minHeight: `${minHeight}px` }}
    >
      <AdComponent
        {...props}
        key={`${slotConfigId}-${refreshCount}`}
        refreshCount={refreshCount}
        onAdViewed={() => {
          handleViewChange("viewed");
          onAdViewed?.();
        }}
        onNotFilled={() => {
          handleViewChange("notFilled");
          setMinHeight(0);
          onNotFilled?.();
        }}
        onFilled={(event: googletag.events.SlotRenderEndedEvent) => {
          const height = isSizeDimension(event.size) ? event.size[1] : adHeight;
          height !== undefined && setMinHeight(height);
          onFilled?.(event);
        }}
      />
    </div>
  );
}
